import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import {
  Calendar,
  List,
  Kanban,
  Filter,
  Search,
  ChevronDown,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Table,
  Pause,
  Settings,
  FileText,
  Activity,
  X,
} from "lucide-react";
import "./EnquiryStageHistory.scss";
import ChecklistModal from "../../pages/Enquiries/ChecklistModal";
import WarningPopup from "../WarningPopup/WarningPopup";
import { toast } from "react-toastify";
import ButtonWithIcon from "../ButtonwithIcon/ButtonWithIcon";
import ProductDetailsModal from "./ProductDetailsModal";
import { Icon } from "@iconify/react";

// Import separated components
import KanbanCard from "./KanbanCard";
import ListItemComponent from "./ListItemComponent";
import TableView from "./TableView";
import { useScrollPreservation } from "../../hooks/useScrollPreservation";
import EnquiryOverviewPanel from "./EnquiryOverviewPanel";
import IconButton from "../IconButton/IconButton";

// ─── Date filter helpers ──────────────────────────────────────────────────────

// Stable default for the `loadingStageIds` prop so an unset prop doesn't
// create a brand-new Set (and therefore a new reference) on every render.
const EMPTY_LOADING_STAGE_IDS = new Set();

const DEFAULT_FILTERS = {
  priority: "all",
  stage: "all",
  datePreset: "all",
  customDateFrom: "",
  customDateTo: "",
  employee: "all",
  lead: "all",
  client: "all",
};

// Dropdown with a type-to-search box, used for the (long) lead/client lists.
// value === "all" means "no filter"; options are { value, label }.
const SearchableSelect = ({ value, options, allLabel, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? options.filter((o) => o.label.toLowerCase().includes(q))
      : options;
  }, [options, query]);

  const selected = options.find((o) => o.value === value);
  const isActive = value !== "all";

  const choose = (next) => {
    onChange(next);
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    } else if (e.key === "Enter" && filteredOptions.length > 0) {
      e.preventDefault();
      choose(filteredOptions[0].value);
    }
  };

  return (
    <div
      ref={wrapRef}
      className={`searchable-select${open ? " open" : ""}${isActive ? " has-value" : ""}`}
    >
      <button
        type="button"
        className="searchable-select-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="searchable-select-label">
          {isActive ? selected?.label || value : allLabel}
        </span>
        <ChevronDown className="searchable-select-chevron" size={16} />
      </button>
      {open && (
        <div className="searchable-select-menu">
          <div className="searchable-select-search">
            <Search size={14} />
            <input
              autoFocus
              type="text"
              placeholder="Search..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <ul role="listbox">
            <li
              role="option"
              aria-selected={!isActive}
              className={!isActive ? "selected" : ""}
              onClick={() => choose("all")}
            >
              {allLabel}
            </li>
            {filteredOptions.map((o) => (
              <li
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                className={o.value === value ? "selected" : ""}
                onClick={() => choose(o.value)}
              >
                {o.label}
              </li>
            ))}
            {filteredOptions.length === 0 && (
              <li className="empty">No matches</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const startOfDay = (d) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};

const DATE_PRESETS = [
  { value: "all", label: "Any Date" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "last30", label: "Last 30 Days" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "overdue", label: "Overdue (past due date)" },
  { value: "dueToday", label: "Due Today" },
  { value: "dueThisWeek", label: "Due This Week" },
  { value: "custom", label: "Custom Range…" },
];

/**
 * Returns true if `dateString` falls within the selected date preset/range,
 * using `dueDateString` for due-date-specific presets.
 */
const matchesDateFilter = (
  preset,
  dateString,
  dueDateString,
  customFrom,
  customTo,
) => {
  if (preset === "all") return true;

  const now = new Date();
  const todayStart = startOfDay(now);

  // Due-date presets
  if (preset === "overdue") {
    if (!dueDateString) return false;
    return new Date(dueDateString) < now;
  }
  if (preset === "dueToday") {
    if (!dueDateString) return false;
    const d = startOfDay(new Date(dueDateString));
    return d.getTime() === todayStart.getTime();
  }
  if (preset === "dueThisWeek") {
    if (!dueDateString) return false;
    const due = new Date(dueDateString);
    const weekEnd = new Date(todayStart);
    weekEnd.setDate(weekEnd.getDate() + 7);
    return due >= todayStart && due < weekEnd;
  }

  // Created-at presets
  if (!dateString) return false;
  const created = new Date(dateString);

  if (preset === "today") {
    return startOfDay(created).getTime() === todayStart.getTime();
  }
  if (preset === "yesterday") {
    const yStart = new Date(todayStart);
    yStart.setDate(yStart.getDate() - 1);
    return startOfDay(created).getTime() === yStart.getTime();
  }
  if (preset === "last7") {
    const cutoff = new Date(todayStart);
    cutoff.setDate(cutoff.getDate() - 7);
    return created >= cutoff;
  }
  if (preset === "last30") {
    const cutoff = new Date(todayStart);
    cutoff.setDate(cutoff.getDate() - 30);
    return created >= cutoff;
  }
  if (preset === "thisMonth") {
    return (
      created.getMonth() === now.getMonth() &&
      created.getFullYear() === now.getFullYear()
    );
  }
  if (preset === "lastMonth") {
    const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return (
      created.getMonth() === lm.getMonth() &&
      created.getFullYear() === lm.getFullYear()
    );
  }
  if (preset === "custom") {
    const from = customFrom ? new Date(customFrom) : null;
    const to = customTo ? new Date(customTo) : null;
    if (from && created < from) return false;
    if (to) {
      const toEnd = new Date(to);
      toEnd.setHours(23, 59, 59, 999);
      if (created > toEnd) return false;
    }
    return true;
  }

  return true;
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// The server's created-date endpoint accepts one start date. Date presets
// still receive the existing client-side end-date filtering after that fetch.
const getStartDateForPreset = (preset, customDateFrom) => {
  if (
    preset === "all" ||
    preset === "overdue" ||
    preset === "dueToday" ||
    preset === "dueThisWeek"
  )
    return null;
  if (preset === "custom") return customDateFrom || null;

  const start = startOfDay(new Date());
  if (preset === "yesterday") start.setDate(start.getDate() - 1);
  if (preset === "last7") start.setDate(start.getDate() - 7);
  if (preset === "last30") start.setDate(start.getDate() - 30);
  if (preset === "thisMonth") start.setDate(1);
  if (preset === "lastMonth") {
    start.setDate(1);
    start.setMonth(start.getMonth() - 1);
  }
  return toDateInputValue(start);
};

// ─────────────────────────────────────────────────────────────────────────────

const EnquiryStageHistory = ({
  data = [],
  // Lightweight per-stage totals from the `.../summary` endpoint:
  //   [{ enquiryStageId, stageName, totalEnquiries, totalQuotationAmount, histories }]
  // Fetched independently of (and ahead of) the paginated per-stage detail
  // fetch that populates `data`, so kanban column headers can show accurate
  // counts/amounts right away instead of being limited to whatever page of
  // cards has loaded so far.
  stageSummary = [],
  enquiryStages = [],
  loading = false,
  onRefresh,
  onStageUpdate,
  onChecklistComplete,
  onChecklistQuickComplete,
  onProductEdit,
  onCreateNew,
  showCreateForm,
  onOpenMOM,
  isWebSocketConnected = false,
  isTeamView = false,
  onAddProducts,
  getApiEndpoint,
  axiosInstance,
  // Per-column ("per-stage") pagination — the backend paginates each
  // stage's card list independently, so each Kanban column tracks and
  // requests its own next page as the user scrolls it.
  //   paginationByStage[stageId] = { offset, hasMore }
  //   loadingStageIds = Set of stageIds currently fetching their next page
  //   onLoadMoreForStage(stageId) = fetch that column's next page
  paginationByStage = {},
  loadingStageIds = EMPTY_LOADING_STAGE_IDS,
  onLoadMoreForStage,
  // Entity lists carry the ids required by the server-side lead/client
  // filter endpoints. The card-list API intentionally only returns names.
  leadOptions = [],
  clientOptions = [],
  onServerFilter,
}) => {
  const [viewMode, setViewMode] = useState("kanban");
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [stageMap, setStageMap] = useState({});
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [loadingItems, setLoadingItems] = useState(new Set());
  const hasInitializedServerFilters = useRef(false);
  const { saveAllKanbanScrolls, restoreAllKanbanScrolls } =
    useScrollPreservation();

  const [warningPopup, setWarningPopup] = useState({
    isOpen: false,
    title: "",
    message: "",
    onAccept: null,
    onDecline: null,
  });

  const [productDetailsModal, setProductDetailsModal] = useState({
    isOpen: false,
    productHistory: null,
  });

  const [checklistModal, setChecklistModal] = useState({
    isOpen: false,
    checklistId: null,
    remarks: "",
  });

  // Create stage mapping
  useEffect(() => {
    if (enquiryStages?.length > 0) {
      const mapping = {};
      enquiryStages.forEach((stage) => {
        mapping[stage.enquiryStageId] = stage;
      });
      setStageMap(mapping);
    }
  }, [enquiryStages]);

  // Stable callback functions
  const handleToggleTasks = useCallback((itemId) => {
    setExpandedTasks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) newSet.delete(itemId);
      else newSet.add(itemId);
      return newSet;
    });
  }, []);

  // Infinite scroll for a single Kanban column. Each column's
  // `.kanban-column-content` fires this on scroll; once the user is within
  // ~150px of the bottom, and that stage actually has more pages and isn't
  // already fetching one, ask the parent to load the next page for just
  // this stageId.
  const handleColumnScroll = useCallback(
    (stageId) => (e) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
      if (!nearBottom) return;

      const stagePagination = paginationByStage[stageId];
      if (!stagePagination?.hasMore) return;
      if (loadingStageIds.has(stageId)) return;

      onLoadMoreForStage?.(stageId);
    },
    [paginationByStage, loadingStageIds, onLoadMoreForStage],
  );

  const handleQuickComplete = useCallback(
    async (checklistId, itemId) => {
      saveAllKanbanScrolls();
      const isPlaceholder =
        checklistId && checklistId.toString().startsWith("placeholder-");
      setLoadingItems((prev) => new Set(prev).add(itemId));
      try {
        if (isPlaceholder) {
          const actualChecklistId = checklistId.replace("placeholder-", "");
          if (onChecklistComplete) {
            await onChecklistComplete(actualChecklistId, "", itemId);
          }
        } else {
          if (onChecklistQuickComplete) {
            await onChecklistQuickComplete(checklistId, "", itemId);
          }
        }
        // The `data` prop (and therefore item.completedTasks/totalTasks that
        // canMoveToNextStage/handleDragEnd gate on) needs to stay current
        // after a completion. Every onChecklistComplete/onChecklistQuickComplete
        // implementation (TeamEnquiry's and Enquiries' completeChecklistItem)
        // now updates `data` in-place itself, so calling the blanket
        // onRefresh() here would only add an unnecessary full board refetch
        // (every stage/column) for a single checklist tick.
      } finally {
        setLoadingItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
        setTimeout(() => {
          restoreAllKanbanScrolls();
        }, 0);
      }
    },
    [
      onChecklistQuickComplete,
      onChecklistComplete,
      saveAllKanbanScrolls,
      restoreAllKanbanScrolls,
    ],
  );

  const updateModalRemarks = useCallback((remarks) => {
    setChecklistModal((prev) => ({ ...prev, remarks }));
  }, []);

  const handleModalClose = useCallback(() => {
    setChecklistModal({
      isOpen: false,
      checklistId: null,
      remarks: "",
      itemId: null,
    });
  }, []);

  const handleModalComplete = useCallback(async () => {
    if (onChecklistComplete) {
      const success = await onChecklistComplete(
        checklistModal.checklistId,
        checklistModal.remarks,
        checklistModal.itemId,
      );
      if (success) {
        setChecklistModal({
          isOpen: false,
          checklistId: null,
          remarks: "",
          itemId: null,
        });
        // Same reasoning as handleQuickComplete: completedTasks/totalTasks
        // is already brought current in-place by onChecklistComplete, so no
        // blanket refetch is needed here.
      }
    }
  }, [
    checklistModal.checklistId,
    checklistModal.remarks,
    checklistModal.itemId,
    onChecklistComplete,
  ]);

  const handleProductClick = useCallback((productHistory) => {
    setProductDetailsModal({ isOpen: true, productHistory });
  }, []);

  const handleCloseProductModal = useCallback(() => {
    setProductDetailsModal({ isOpen: false, productHistory: null });
  }, []);

  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case "COMPLETED":
        return <CheckCircle className="status-icon completed" size={16} />;
      case "IN_PROGRESS":
        return <Clock className="status-icon in-progress" size={16} />;
      case "CANCELLED":
        return <XCircle className="status-icon cancelled" size={16} />;
      case "PENDING":
        return <Pause className="status-icon pending" size={16} />;
      default:
        return <AlertCircle className="status-icon default" size={16} />;
    }
  }, []);

  const getStageTypeIcon = useCallback((stageType) => {
    if (!stageType)
      return <FileText className="stage-type-icon default" size={16} />;

    switch (stageType.toUpperCase()) {
      case "INITIAL":
        return <Activity className="stage-type-icon initial" size={16} />;
      case "QUOTATION":
        return <FileText className="stage-type-icon quotation" size={16} />;
      case "NEGOTIATION":
        return <Settings className="stage-type-icon negotiation" size={16} />;
      case "WON":
        return (
          <CheckCircle
            className="stage-type-icon won"
            size={16}
            color="green"
          />
        );
      case "LOST":
        return (
          <XCircle className="stage-type-icon lost" size={16} color="red" />
        );
      case "OTHERS":
      default:
        return <FileText className="stage-type-icon default" size={16} />;
    }
  }, []);

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  }, []);

  const isChecklistComplete = useCallback((item) => {
    if (!item.totalTasks || item.totalTasks === 0) return true;
    return item.completedTasks === item.totalTasks;
  }, []);

  const canMoveToNextStage = useCallback(
    (item) => {
      if (!enquiryStages?.length)
        return { canMove: false, reason: "Stages not loaded" };
      if (!item.totalTasks || item.totalTasks === 0) {
        return { canMove: true, reason: null };
      }
      if (!isChecklistComplete(item)) {
        return {
          canMove: false,
          reason: "Please complete all checklist items before proceeding",
        };
      }
      return { canMove: true, reason: null };
    },
    [enquiryStages, isChecklistComplete],
  );

  const filterDropdownOptions = useMemo(() => {
    const employees = new Set();
    const leads = new Set();
    const clients = new Set();

    data.forEach((stageGroup) => {
      stageGroup.histories?.forEach((h) => {
        if (h.assignedTo) employees.add(h.assignedTo);
        if (h.leadName) leads.add(h.leadName);
        if (h.clientName) clients.add(h.clientName);
      });
    });

    const optionFromEntity = (entity) => ({
      value: String(entity.id),
      label: entity.companyName || entity.name || "Unnamed",
    });

    return {
      employees: Array.from(employees).sort(),
      leads:
        leadOptions.length > 0
          ? leadOptions.map(optionFromEntity)
          : Array.from(leads)
              .sort()
              .map((label) => ({ value: label, label })),
      clients:
        clientOptions.length > 0
          ? clientOptions.map(optionFromEntity)
          : Array.from(clients)
              .sort()
              .map((label) => ({ value: label, label })),
    };
  }, [data, leadOptions, clientOptions]);

  const selectedLeadLabel =
    filterDropdownOptions.leads.find((option) => option.value === filters.lead)
      ?.label || filters.lead;
  const selectedClientLabel =
    filterDropdownOptions.clients.find(
      (option) => option.value === filters.client,
    )?.label || filters.client;

  useEffect(() => {
    // The parent performs the normal initial board fetch. Skipping this first
    // effect prevents a second identical request on mount.
    if (!hasInitializedServerFilters.current) {
      hasInitializedServerFilters.current = true;
      return;
    }
    if (!onServerFilter) return;

    // Number("Acme") is NaN, which is falsy and would silently fall through
    // to the "all" branch, so guard against non-numeric option values.
    const toId = (value) => {
      if (value === "all" || value === "" || value == null) return null;
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };
    const stageId = toId(filters.stage);
    const clientId = toId(filters.client);
    const leadId = toId(filters.lead);
    const startDate = getStartDateForPreset(
      filters.datePreset,
      filters.customDateFrom,
    );

    // Client/lead endpoints need a stageId. If no stage is picked, the
    // parent fans out across every stage (stageId === null).
    // Client wins only because selecting one clears the other above.
    if (clientId) {
      onServerFilter({ type: "client", clientId, stageId });
    } else if (leadId) {
      onServerFilter({ type: "lead", leadId, stageId });
    } else if (startDate) {
      onServerFilter({ type: "startDate", startDate });
    } else {
      onServerFilter({ type: "all" });
    }
  }, [
    filters.stage,
    filters.client,
    filters.lead,
    filters.datePreset,
    filters.customDateFrom,
    onServerFilter,
  ]);

  const transformedAndFilteredData = useMemo(() => {
    const transformed = [];
    data.forEach((stageGroup) => {
      const stageTemplate = stageMap[stageGroup.enquiryStageId] || {};
      const authoritativeStageType = stageGroup.stageType;
      const customPricing = stageGroup.customPricing || false;
      const isEffectivelyFinal =
        stageTemplate.finalStage ||
        ["WON", "LOST"].includes(authoritativeStageType);
      const combinedStageDetails = {
        ...stageTemplate,
        stageType: authoritativeStageType,
        finalStage: isEffectivelyFinal,
      };

      stageGroup.histories?.forEach((historyItem) => {
        const actualChecklistHistories = historyItem.checkListHistories || [];
        let displayChecklistItems;
        if (
          actualChecklistHistories.length === 0 &&
          combinedStageDetails.checkLists?.length > 0
        ) {
          displayChecklistItems = combinedStageDetails.checkLists.map(
            (templateTask) => ({
              id: `placeholder-${templateTask.id}`,
              checkListId: templateTask.id,
              taskName: templateTask.checkListName,
              remarks: "",
              completed: false,
              isPlaceholder: true,
            }),
          );
        } else {
          displayChecklistItems = actualChecklistHistories.map(
            (historyTask) => ({
              ...historyTask,
              taskName:
                historyTask.checklistName ||
                historyTask.checkListName ||
                "Unnamed Task",
            }),
          );
        }

        // Prefer the counts the API already computed on the history item
        // itself (e.g. completedTasks: 7, totalTasks: 7) — some endpoints
        // return these directly without a checkListHistories array. Only
        // fall back to counting displayChecklistItems when the API didn't
        // send explicit counts, so we don't silently zero out real progress.
        const apiHasTaskCounts =
          historyItem.completedTasks !== undefined &&
          historyItem.totalTasks !== undefined;

        const completedTasks = apiHasTaskCounts
          ? historyItem.completedTasks
          : displayChecklistItems.filter((c) => c.completed)?.length || 0;
        const totalTasks = apiHasTaskCounts
          ? historyItem.totalTasks
          : displayChecklistItems.length || 0;

        const productHistories = (
          historyItem.productEnquiryHistories || []
        ).map((peh) => ({
          id: peh.id,
          productId: peh.productId,
          productName: peh.productName,
          skuCode: peh.skuCode,
          quantity: peh.quantity,
          fulfillmentStatus: peh.fulfillmentStatus || "PENDING",
          price: peh.price,
          specificationHistories: (peh.specificationHistories || []).map(
            (sh) => ({
              id: sh.id,
              productSpecificationId: sh.productSpecificationId,
              productSpecificationValueId: sh.productSpecificationValueId,
              specsName: sh.specsName,
              valueName: sh.valueName,
            }),
          ),
        }));

        transformed.push({
          id: historyItem.id,
          stageName: stageGroup.stageName,
          stageId: stageGroup.enquiryStageId,
          status: historyItem.enquiryStatus,
          remarks: historyItem.remarks || "",
          completedTasks,
          totalTasks,
          checkListHistories: displayChecklistItems,
          progress: totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0,
          priority: historyItem.priority,
          stageDetails: combinedStageDetails,
          customPricing: customPricing,
          createdAt: historyItem.createdAt,
          updatedAt: historyItem.updatedAt,
          startedAt: historyItem.startedAt,
          endedAt: historyItem.endedAt,
          dueAt: historyItem.dueAt,
          leadName: historyItem.leadName,
          leadCode: historyItem.leadCode,
          clientName: historyItem.clientName,
          clientCode: historyItem.clientCode,
          assignedTo: historyItem.assignedTo,
          assignedEmployeeId: historyItem.assignedEmployeeId,
          notes: historyItem.notes,
          productHistories: productHistories,
          enquiryId: historyItem.enquiryId,
          enquiryStageName: historyItem.enquiryStageName,
        });
      });
    });

    const sorted = transformed.sort((a, b) => {
      const seqA = a.stageDetails.sequenceNumber || 999;
      const seqB = b.stageDetails.sequenceNumber || 999;
      return seqA - seqB;
    });

    return sorted.filter((item) => {
      if (filters.stage !== "all" && item.stageId.toString() !== filters.stage)
        return false;
      if (filters.priority !== "all" && item.priority !== filters.priority) {
        return false;
      }

      if (filters.employee !== "all" && item.assignedTo !== filters.employee)
        return false;

      if (filters.lead !== "all" && item.leadName !== selectedLeadLabel)
        return false;

      if (filters.client !== "all" && item.clientName !== selectedClientLabel)
        return false;

      // ── Date filter ────────────────────────────────────────────────────────
      if (
        !matchesDateFilter(
          filters.datePreset,
          item.createdAt,
          item.dueAt,
          filters.customDateFrom,
          filters.customDateTo,
        )
      ) {
        return false;
      }

      return true;
    });
  }, [data, filters, stageMap, selectedLeadLabel, selectedClientLabel]);

  // Finds which card (enquiryStageHistoryId) a given checklist entry belongs
  // to, by looking it up in the already-transformed/rendered data. This lets
  // us know the owning card's id even when the caller (KanbanCard /
  // ListItemComponent) only passes the checklistId, so a targeted
  // single-card refresh can be requested after completion instead of
  // reloading everything.
  const resolveItemIdForChecklist = useCallback(
    (checklistId) => {
      if (checklistId == null) return null;
      for (const item of transformedAndFilteredData) {
        if (item.checkListHistories?.some((c) => c.id === checklistId)) {
          return item.id;
        }
      }
      return null;
    },
    [transformedAndFilteredData],
  );

  const openChecklistModal = useCallback(
    (checklistId, isPlaceholder = false, itemId = null) => {
      setChecklistModal({
        isOpen: true,
        checklistId: checklistId,
        remarks: "",
        isPlaceholder: isPlaceholder,
        itemId: itemId ?? resolveItemIdForChecklist(checklistId),
      });
    },
    [resolveItemIdForChecklist],
  );

  const handleMoveToNextStage = useCallback(
    async (item) => {
      const validation = canMoveToNextStage(item);
      if (!validation.canMove) {
        toast.error(validation.reason);
        return;
      }
      const nextStage = enquiryStages.find(
        (stage) =>
          stage.sequenceNumber === item.stageDetails.sequenceNumber + 1,
      );
      if (!nextStage) {
        toast.error(
          "Could not find the next stage. This may be the final stage.",
        );
        return;
      }
      try {
        if (onStageUpdate) {
          await onStageUpdate(item.id, nextStage.enquiryStageId);
          if (onRefresh) onRefresh();
        }
      } catch (error) {
        console.error("Failed to update stage:", error);
      }
    },
    [canMoveToNextStage, onStageUpdate, onRefresh, enquiryStages],
  );

  const handleStageProgression = useCallback(
    async (item) => {
      const validation = canMoveToNextStage(item);
      if (!validation.canMove) {
        setWarningPopup({
          isOpen: true,
          title: "Cannot Move Stage",
          message: validation.reason,
          acceptText: "OK",
          declineText: "",
          onAccept: () =>
            setWarningPopup((prev) => ({ ...prev, isOpen: false })),
          onDecline: null,
        });
        return;
      }
      const forwardStages = enquiryStages
        .filter(
          (stage) => stage.sequenceNumber > item.stageDetails.sequenceNumber,
        )
        .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
      if (forwardStages.length === 0) {
        setWarningPopup({
          isOpen: true,
          title: "Error",
          message: "No forward stages available. This may be the final stage.",
          acceptText: "OK",
          onAccept: () =>
            setWarningPopup((prev) => ({ ...prev, isOpen: false })),
        });
        return;
      }
      setWarningPopup({
        isOpen: true,
        title: "Select Target Stage",
        message: (
          <div className="stage-selection">
            <p>Select which stage to move this enquiry to:</p>
            <select
              id="target-stage-select"
              className="stage-select-dropdown"
              defaultValue=""
            >
              <option value="" disabled>
                -- Select Stage --
              </option>
              {forwardStages.map((stage) => (
                <option key={stage.enquiryStageId} value={stage.enquiryStageId}>
                  Step {stage.sequenceNumber}: {stage.stageName}
                </option>
              ))}
            </select>
          </div>
        ),
        acceptText: "Move",
        declineText: "Cancel",
        onAccept: async () => {
          const selectElement = document.getElementById("target-stage-select");
          const selectedStageId = selectElement?.value;
          if (!selectedStageId) {
            toast.error("Please select a target stage");
            return;
          }
          try {
            if (onStageUpdate) {
              await onStageUpdate(item.id, Number(selectedStageId));
              if (onRefresh) onRefresh();
            }
            setWarningPopup((prev) => ({ ...prev, isOpen: false }));
          } catch (error) {
            console.error("Failed to update stage:", error);
          }
        },
        onDecline: () =>
          setWarningPopup((prev) => ({ ...prev, isOpen: false })),
      });
    },
    [canMoveToNextStage, onStageUpdate, onRefresh, enquiryStages],
  );

  const handleToggleDetails = useCallback((itemId) => {
    setSelectedItem((prevId) => (prevId === itemId ? null : itemId));
  }, []);

  const handleDragEnd = useCallback(
    async (result) => {
      const { source, destination, draggableId } = result;
      if (!destination) return;
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      )
        return;
      if (source.droppableId === destination.droppableId) return;

      const itemId = draggableId.replace("item-", "");
      const targetStageId = Number(
        destination.droppableId.replace("stage-", ""),
      );
      const draggedItem = transformedAndFilteredData.find(
        (item) => item.id.toString() === itemId,
      );
      const targetStage = enquiryStages.find(
        (stage) => stage.enquiryStageId === targetStageId,
      );
      if (!draggedItem || !targetStage) {
        toast.error("Could not process the move. Item or stage not found.");
        return;
      }
      const validation = canMoveToNextStage(draggedItem);
      if (!validation.canMove) {
        toast.error(validation.reason || "Cannot move to this stage.");
        return;
      }
      try {
        if (onStageUpdate) {
          await onStageUpdate(draggedItem.id, targetStageId);
          if (onRefresh) onRefresh();
        }
      } catch (error) {
        console.error("Failed to update stage via drag-and-drop:", error);
        toast.error("Failed to move enquiry. Please try again.");
        if (onRefresh) onRefresh();
      }
    },
    [
      transformedAndFilteredData,
      enquiryStages,
      canMoveToNextStage,
      onStageUpdate,
      onRefresh,
    ],
  );

  const handleToggleCardExpanded = useCallback(
    (itemId) => {
      // Save the kanban board's horizontal scroll before the expand re-render
      const kanbanBoard = document.querySelector(".kanban-view.enhanced");
      const savedScrollLeft = kanbanBoard ? kanbanBoard.scrollLeft : 0;

      saveAllKanbanScrolls();
      setExpandedCards((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(itemId)) newSet.delete(itemId);
        else newSet.add(itemId);
        return newSet;
      });

      // Restore both vertical column scrolls and horizontal board scroll
      const restoreAll = () => {
        restoreAllKanbanScrolls();
        const board = document.querySelector(".kanban-view.enhanced");
        if (board) board.scrollLeft = savedScrollLeft;
      };

      setTimeout(restoreAll, 0);
      setTimeout(restoreAll, 50);
    },
    [saveAllKanbanScrolls, restoreAllKanbanScrolls],
  );

  // ── View components ──────────────────────────────────────────────────────────

  const ListView = useCallback(
    () => (
      <div className="list-view enhanced">
        {transformedAndFilteredData.map((item) => {
          const canMove = canMoveToNextStage(item);
          return (
            <ListItemComponent
              key={item.id}
              item={item}
              isSelected={selectedItem === item.id}
              canMoveToNext={canMove.canMove}
              nextStageReason={canMove.reason}
              onToggleDetails={handleToggleDetails}
              onStageProgression={handleStageProgression}
              onMoveToImmediateNext={handleMoveToNextStage}
              onProductEdit={onProductEdit}
              onProductClick={handleProductClick}
              onOpenModal={openChecklistModal}
              onQuickComplete={handleQuickComplete}
              expandedTasks={expandedTasks}
              onToggleTasks={handleToggleTasks}
              loadingItems={loadingItems}
              getStatusIcon={getStatusIcon}
              getStageTypeIcon={getStageTypeIcon}
              isChecklistComplete={isChecklistComplete}
              formatDateTime={formatDateTime}
              customPricing={item.customPricing}
              onOpenMOM={onOpenMOM}
              isTeamView={isTeamView}
              onAddProducts={onAddProducts}
              getApiEndpoint={getApiEndpoint}
            />
          );
        })}
        {transformedAndFilteredData.length === 0 && !loading && (
          <div className="empty-state">
            <AlertCircle className="empty-icon" />
            <p>No enquiries found matching your filters</p>
          </div>
        )}
      </div>
    ),
    [
      transformedAndFilteredData,
      selectedItem,
      canMoveToNextStage,
      handleToggleDetails,
      handleStageProgression,
      handleMoveToNextStage,
      openChecklistModal,
      handleQuickComplete,
      getStatusIcon,
      getStageTypeIcon,
      isChecklistComplete,
      handleProductClick,
      formatDateTime,
      loading,
      onProductEdit,
      expandedTasks,
      handleToggleTasks,
      loadingItems,
      onOpenMOM,
      isTeamView,
      onAddProducts,
      getApiEndpoint,
    ],
  );

  const KanbanView = useCallback(() => {
    const getItemsByStage = (stageId) =>
      transformedAndFilteredData.filter((item) => item.stageId === stageId);

    const columns =
      enquiryStages?.length > 0
        ? enquiryStages.sort((a, b) => a.sequenceNumber - b.sequenceNumber)
        : [];

    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="kanban-view enhanced">
          {columns.map((stage, columnIndex) => {
            const items = getItemsByStage(stage.enquiryStageId);
            // 1. Find the matching stage group from the raw data
            const stageGroupData = data.find(
              (d) => d.enquiryStageId === stage.enquiryStageId,
            );
            // 1b. Prefer the dedicated summary endpoint's totals for this
            // stage — it reflects ALL enquiries in the stage, not just
            // whatever page(s) of cards have loaded so far.
            const stageSummaryData = stageSummary.find(
              (s) => s.enquiryStageId === stage.enquiryStageId,
            );
            // 2. Extract the totalQuotationAmount (default to 0 if undefined)
            const totalAmount =
              stageSummaryData?.totalQuotationAmount ??
              stageGroupData?.totalQuotationAmount ??
              0;
            // 3. Prefer the summary's true total count over the number of
            // cards currently loaded into this column.
            const totalCount = stageSummaryData?.totalEnquiries ?? items.length;
            return (
              <Droppable
                key={stage.enquiryStageId}
                droppableId={`stage-${stage.enquiryStageId}`}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`kanban-column ${stage.stageType?.toLowerCase() || ""} ${snapshot.isDraggingOver ? "drag-over" : ""}`}
                  >
                    <div className="kanban-column-header">
                      <div className="column-title">
                        {stage.stageType && getStageTypeIcon(stage.stageType)}
                        <div className="column-info">
                          <span className="stage-name">{stage.stageName}</span>
                          <span
                            className="stage-amount"
                            style={{
                              display: "block",
                              fontSize: "0.85rem",
                              opacity: 0.8,
                              marginTop: "2px",
                              fontWeight: 600,
                            }}
                          >
                            ₹
                            {totalAmount.toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </div>
                      <span className="item-count">{totalCount}</span>
                    </div>
                    <div
                      className="kanban-column-content"
                      data-container={`kanban-column-${columnIndex}`}
                      onScroll={handleColumnScroll(stage.enquiryStageId)}
                    >
                      {items.map((item, index) => {
                        const canMove = canMoveToNextStage(item);
                        return (
                          <KanbanCard
                            key={item.id}
                            item={item}
                            index={index}
                            canMoveToNext={canMove.canMove}
                            nextStageReason={canMove.reason}
                            onStageProgression={handleStageProgression}
                            onMoveToImmediateNext={handleMoveToNextStage}
                            isChecklistComplete={isChecklistComplete}
                            onQuickComplete={handleQuickComplete}
                            getStatusIcon={getStatusIcon}
                            onOpenModal={openChecklistModal}
                            onProductClick={handleProductClick}
                            formatDateTime={formatDateTime}
                            onProductEdit={onProductEdit}
                            isExpanded={expandedCards.has(item.id)}
                            onToggleExpanded={handleToggleCardExpanded}
                            loadingItems={loadingItems}
                            onOpenMOM={onOpenMOM}
                            isTeamView={isTeamView}
                            onAddProducts={onAddProducts}
                            getApiEndpoint={getApiEndpoint}
                          />
                        );
                      })}
                      {items.length === 0 && (
                        <div
                          className={`empty-column ${snapshot.isDraggingOver ? "drag-over" : ""}`}
                        >
                          <p>No items</p>
                        </div>
                      )}
                      {loadingStageIds.has(stage.enquiryStageId) && (
                        <div
                          className="kanban-column-loading-more"
                          style={{
                            textAlign: "center",
                            padding: "0.5rem",
                            fontSize: "0.85rem",
                            opacity: 0.7,
                          }}
                        >
                          Loading more…
                        </div>
                      )}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
    );
  }, [
    transformedAndFilteredData,
    enquiryStages,
    canMoveToNextStage,
    handleStageProgression,
    handleMoveToNextStage,
    isChecklistComplete,
    handleQuickComplete,
    getStatusIcon,
    getStageTypeIcon,
    handleDragEnd,
    onProductEdit,
    expandedCards,
    openChecklistModal,
    handleProductClick,
    formatDateTime,
    handleToggleCardExpanded,
    onOpenMOM,
    isTeamView,
    loadingItems,
    onAddProducts,
    getApiEndpoint,
    data,
    stageSummary,
    handleColumnScroll,
    loadingStageIds,
  ]);

  const CalendarView = useCallback(() => {
    const itemsByDate = transformedAndFilteredData.reduce((acc, item) => {
      const date = new Date(item.createdAt).toDateString();
      if (!acc[date]) acc[date] = [];
      acc[date].push(item);
      return acc;
    }, {});
    const sortedDates = Object.keys(itemsByDate).sort(
      (a, b) => new Date(b) - new Date(a),
    );
    return (
      <div className="calendar-view">
        {sortedDates.length === 0 ? (
          <div className="calendar-placeholder">
            <Calendar className="calendar-icon" />
            <h3>Calendar View</h3>
            <p>No enquiries scheduled for viewing</p>
          </div>
        ) : (
          <>
            <div className="calendar-header">
              <h3>Enquiry Timeline</h3>
              <p className="calendar-subtitle">
                Showing {transformedAndFilteredData.length} enquiries across{" "}
                {sortedDates.length} days
              </p>
            </div>
            <div className="calendar-timeline">
              {sortedDates.map((date) => (
                <div key={date} className="timeline-day">
                  <div className="timeline-header">
                    <div className="day-title">
                      {new Date(date).toLocaleDateString("en-IN", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </div>
                    <div className="item-count">
                      {itemsByDate[date].length} enquiry
                      {itemsByDate[date].length !== 1 ? "ies" : ""}
                    </div>
                  </div>
                  <div className="timeline-items">
                    {itemsByDate[date].map((item) => (
                      <div key={item.id} className="calendar-item">
                        {getStatusIcon(item.status)}
                        <div className="calendar-item-content">
                          <div className="calendar-item-header">
                            <span className="item-stage">{item.stageName}</span>
                            <span className="item-enquiry">
                              #{item.enquiryId}
                            </span>
                          </div>
                          <div className="calendar-item-details">
                            {(item.leadName || item.clientName) && (
                              <div className="client-info-inline">
                                <span className="client-name">
                                  {item.leadName || item.clientName}
                                </span>
                                <span
                                  className={`client-type-badge ${item.leadName ? "new-client" : "existing-client"}`}
                                >
                                  {item.leadName ? "New Client" : "Existing"}
                                </span>
                              </div>
                            )}
                            {isTeamView && item.assignedTo && (
                              <span className="item-assigned">
                                Assigned: {item.assignedTo}
                              </span>
                            )}
                            {item.productHistories &&
                              item.productHistories.length > 0 && (
                                <span className="products-info">
                                  {item.productHistories.length} Product(s) •{" "}
                                  {item.productHistories.reduce(
                                    (sum, p) => sum + p.quantity,
                                    0,
                                  )}{" "}
                                  qty
                                </span>
                              )}
                          </div>
                          <div className="calendar-item-dates">
                            {item.startedAt && (
                              <span className="item-date">
                                Started: {formatDateTime(item.startedAt)}
                              </span>
                            )}
                            {item.dueAt && (
                              <span className="item-due">
                                Due: {new Date(item.dueAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {item.notes && (
                            <div className="calendar-item-notes">
                              <strong>Notes:</strong>{" "}
                              {item.notes.length > 100
                                ? `${item.notes.substring(0, 100)}...`
                                : item.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }, [transformedAndFilteredData, getStatusIcon, formatDateTime, isTeamView]);

  const renderView = () => {
    switch (viewMode) {
      case "table":
        return (
          <TableView
            data={transformedAndFilteredData}
            onToggleDetails={handleToggleDetails}
            selectedItem={selectedItem}
            onStageProgression={handleStageProgression}
            onMoveToImmediateNext={handleMoveToNextStage}
            onProductEdit={onProductEdit}
            onProductClick={handleProductClick}
            onOpenModal={openChecklistModal}
            onQuickComplete={handleQuickComplete}
            onOpenMOM={onOpenMOM}
            canMoveToNextStage={canMoveToNextStage}
            getStatusIcon={getStatusIcon}
            getStageTypeIcon={getStageTypeIcon}
            isChecklistComplete={isChecklistComplete}
            formatDateTime={formatDateTime}
            loadingItems={loadingItems}
            isTeamView={isTeamView}
            onAddProducts={onAddProducts}
            getApiEndpoint={getApiEndpoint}
          />
        );
      case "kanban":
        return KanbanView(); // <-- Change from <KanbanView /> to KanbanView()
      case "calendar":
        return CalendarView();
      case "overview":
        return (
          <EnquiryOverviewPanel
            axiosInstance={axiosInstance}
            getApiEndpoint={getApiEndpoint}
            isTeamView={isTeamView}
          />
        );
      default:
        return <ListView />;
    }
  };

  // ── Active-filter count badge ────────────────────────────────────────────────
  const activeFilterCount = [
    filters.priority !== "all",
    filters.stage !== "all",
    filters.datePreset !== "all",
    filters.employee !== "all",
    filters.lead !== "all",
    filters.client !== "all",
  ].filter(Boolean).length;

  const clearFilters = () => setFilters({ ...DEFAULT_FILTERS });
  const isActive = (isOn) => (isOn ? " is-active" : "");

  return (
    <div className="enquiry-stage-history enhanced">
      {/* Header */}
      <div className="header">
        <div className="header-left">
          <h1>Enquiries Management</h1>
          <p className="subtitle">
            Handle and track customer enquiries efficiently in one place.
          </p>
        </div>

        <div className="header-actions">
          {/* Filter Toggle */}
          {viewMode !== "overview" && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`filter-toggle ${showFilters ? "active" : ""}`}
            >
              <Filter className="filter-icon" />
              Filters
              {activeFilterCount > 0 && (
                <span className="filter-count-badge">{activeFilterCount}</span>
              )}
              <ChevronDown
                className={`chevron ${showFilters ? "rotated" : ""}`}
              />
            </button>
          )}

          <div className="view-toggles">
            <button
              onClick={() => setViewMode("kanban")}
              className={`view-toggle ${viewMode === "kanban" ? "active" : ""}`}
              title="Kanban View"
            >
              <Kanban className="view-icon" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`view-toggle ${viewMode === "list" ? "active" : ""}`}
              title="List View"
            >
              <List className="view-icon" />
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`view-toggle ${viewMode === "table" ? "active" : ""}`}
              title="Table View"
            >
              <Table className="view-icon" />
            </button>
            <button
              onClick={() => setViewMode("calendar")}
              className={`view-toggle ${viewMode === "calendar" ? "active" : ""}`}
              title="Calendar View"
            >
              <Calendar className="view-icon" />
            </button>
          </div>

          {onCreateNew && (
            <ButtonWithIcon
              icon={showCreateForm ? "mdi:close" : "mdi:plus"}
              text={showCreateForm ? "Hide Form" : "Create Enquiry"}
              onClick={onCreateNew}
              className={showCreateForm ? "cancel-button" : "submit-button"}
            />
          )}

          <ButtonWithIcon
            icon="mdi:view-list-outline"
            text="Overview"
            onClick={() =>
              setViewMode(viewMode === "overview" ? "kanban" : "overview")
            }
            className={viewMode === "overview" ? "edit-button" : ""}
            title="Overview — see all enquiries at a glance"
          />

          {isWebSocketConnected ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "4px 10px",
                borderRadius: 999,
                background: "#dcfce7",
                color: "#15803d",
                border: "1px solid #86efac",
                fontSize: "0.85rem",
                fontWeight: 600,
                width: "fit-content",
              }}
            >
              <Icon icon="mdi:access-point" />
              <span>Live Updates</span>
            </div>
          ) : (
            <IconButton
              icon="mdi:refresh"
              text="Refresh"
              onClick={onRefresh}
              title="Refresh enquiries"
            />
          )}
        </div>
      </div>
      {/* Filters Panel */}
      {viewMode !== "overview" && showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            {/* Status */}
            <div
              className={`filter-group status-group${isActive(filters.priority !== "all")}`}
            >
              <div className="status-toggles">
                {[
                  { id: "all", label: "All Priorities" },
                  { id: "HIGH", label: "High" },
                  { id: "MEDIUM", label: "Medium" },
                  { id: "LOW", label: "Low" },
                ].map((priorityItem) => (
                  <button
                    key={priorityItem.id}
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        priority: priorityItem.id,
                      }))
                    }
                    className={`status-btn ${
                      filters.priority === priorityItem.id ? "active" : ""
                    }`}
                    type="button"
                  >
                    {priorityItem.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Stage */}
            <div className={`filter-group${isActive(filters.stage !== "all")}`}>
              <select
                value={filters.stage}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, stage: e.target.value }))
                }
              >
                <option value="all">All Stages</option>
                {enquiryStages.map((stage) => (
                  <option
                    key={stage.enquiryStageId}
                    value={stage.enquiryStageId}
                  >
                    {stage.stageName} (Step {stage.sequenceNumber})
                  </option>
                ))}
              </select>
            </div>

            {/* Employee Filter — only relevant in the Team view */}
            {isTeamView && (
              <div
                className={`filter-group${isActive(filters.employee !== "all")}`}
              >
                <select
                  value={filters.employee}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      employee: e.target.value,
                    }))
                  }
                >
                  <option value="all">All Employees</option>
                  {filterDropdownOptions.employees.map((emp) => (
                    <option key={emp} value={emp}>
                      {emp}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Lead Filter (searchable) */}
            <div className={`filter-group${isActive(filters.lead !== "all")}`}>
              <SearchableSelect
                value={filters.lead}
                options={filterDropdownOptions.leads}
                allLabel="All Leads"
                onChange={(next) =>
                  setFilters((prev) => ({
                    ...prev,
                    lead: next,
                    client: "all",
                  }))
                }
              />
            </div>

            {/* Client Filter (searchable) */}
            <div
              className={`filter-group${isActive(filters.client !== "all")}`}
            >
              <SearchableSelect
                value={filters.client}
                options={filterDropdownOptions.clients}
                allLabel="All Clients"
                onChange={(next) =>
                  setFilters((prev) => ({
                    ...prev,
                    client: next,
                    // The API has separate lead/client endpoints, so avoid
                    // an ambiguous combined request.
                    lead: "all",
                  }))
                }
              />
            </div>

            {/* ── Date filter ─────────────────────────────────────────────── */}
            <div
              className={`filter-group${isActive(filters.datePreset !== "all")}`}
            >
              <select
                value={filters.datePreset}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    datePreset: e.target.value,
                    // Reset custom range when switching away
                    customDateFrom:
                      e.target.value !== "custom" ? "" : prev.customDateFrom,
                    customDateTo:
                      e.target.value !== "custom" ? "" : prev.customDateTo,
                  }))
                }
              >
                {DATE_PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom date range — only shown when "custom" is selected */}
            {filters.datePreset === "custom" && (
              <>
                <div
                  className={`filter-group${isActive(!!filters.customDateFrom)}`}
                >
                  <label className="filter-label">From</label>
                  <input
                    type="date"
                    className="date-input"
                    value={filters.customDateFrom}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        customDateFrom: e.target.value,
                      }))
                    }
                  />
                </div>
                <div
                  className={`filter-group${isActive(!!filters.customDateTo)}`}
                >
                  <label className="filter-label">To</label>
                  <input
                    type="date"
                    className="date-input"
                    value={filters.customDateTo}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        customDateTo: e.target.value,
                      }))
                    }
                  />
                </div>
              </>
            )}

            {/* Clear */}
            <div className="filter-group">
              <button
                type="button"
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
                className={`clear-filters-btn${activeFilterCount > 0 ? " has-filters" : ""}`}
              >
                <X size={14} />
                Clear Filters
                {activeFilterCount > 0 && (
                  <span className="clear-filters-count">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Main Content */}
      <div className="content">{renderView()}</div>
      <WarningPopup
        isOpen={warningPopup.isOpen}
        title={warningPopup.title}
        message={warningPopup.message}
        acceptText={warningPopup.acceptText}
        declineText={warningPopup.declineText}
        onAccept={warningPopup.onAccept}
        onDecline={
          warningPopup.onDecline ||
          (() => setWarningPopup((prev) => ({ ...prev, isOpen: false })))
        }
        onClose={() => setWarningPopup((prev) => ({ ...prev, isOpen: false }))}
      />
      <ChecklistModal
        isOpen={checklistModal.isOpen}
        remarks={checklistModal.remarks}
        onRemarksChange={updateModalRemarks}
        onClose={handleModalClose}
        onComplete={handleModalComplete}
      />
      <ProductDetailsModal
        isOpen={productDetailsModal.isOpen}
        onClose={handleCloseProductModal}
        productHistory={productDetailsModal.productHistory}
      />
    </div>
  );
};

export default EnquiryStageHistory;
